// src/config/discord.ts

import { getApiBaseUrl, getRedirectUri } from '../utils/environment';
import { getDiscordClientId } from '../utils/backendConfig';
import { webSocketConfig } from './websocket';

/**
 * Discord Configuration - Updated for Backend Integration
 * 
 * SECURITY UPDATE: Discord client ID now fetched from backend API at runtime
 * instead of being stored in frontend environment variables.
 * 
 * This provides better security by:
 * - Keeping sensitive data on the backend only
 * - Fetching public configuration at runtime
 * - Reducing attack surface of frontend bundle
 */

/**
 * Get Discord client ID from backend configuration
 * This replaces the old environment variable approach
 */
export async function getDiscordConfig() {
  const clientId = await getDiscordClientId();
  
  return {
    // Discord OAuth2 Client ID fetched from backend
    clientId: clientId || '', // Fallback to empty string if not available
  
  // OAuth2 Redirect URI
  // This must match the redirect URI configured in the Discord Developer Portal
  redirectUri: getRedirectUri('discord'),
  
  // API Endpoint for backend communication
  // This should point to your Discord bot backend API
  apiEndpoint: getApiBaseUrl(),
  
  // WebSocket Endpoint for real-time communication
  // This should point to your Discord bot WebSocket server
  websocketEndpoint: process.env.REACT_APP_WEBSOCKET_URL || webSocketConfig.endpoint,
  
  // OAuth2 Scopes required by the application
  scopes: [
    'identify',       // Access user's Discord ID, username, and avatar
    'email'           // Access user's email address
  ],
  
  // Bot permissions required by the application
  // This is a bit field value - see https://discord.com/developers/docs/topics/permissions
  botPermissions: '3072' // Send Messages (0x800) + Connect (0x100000) = 3072
  };
}


/**
 * Discord API Routes
 * These routes should match the endpoints exposed by your Discord bot backend
 */
export const discordApiRoutes = {
  // Authentication
  auth: {
    token: '/auth/token',
    refresh: '/auth/refresh',
    logout: '/auth/logout'
  },
  
  // Voice
  voice: {
    connect: '/voice/connect',
    disconnect: '/voice/disconnect',
    chunk: '/voice/chunk'
  },
  
  // State
  state: {
    update: '/state/update',
    sync: '/state/sync'
  },
  
  // Events
  events: {
    send: '/events',
    history: '/events/history'
  },
  
  // User
  user: {
    profile: '/user/profile',
    settings: '/user/settings'
  },
  
  // Guilds (Servers)
  guilds: {
    list: '/guilds',
    join: '/guilds/join',
    channels: '/guilds/{guildId}/channels'
  }
};
